import { useState, useEffect } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { canInstall, promptInstall } from "@/lib/pwaInstall";

// Persistent "Download this app" action. Always available so anyone can install
// (or re-install) the PWA at any time. Falls back to manual instructions when the
// browser can't offer a native prompt (already installed, iOS Safari, etc.).
export default function InstallAppButton({ variant = "sidebar" }) {
  const [, force] = useState(0);
  useEffect(() => {
    const rerender = () => force((n) => n + 1);
    window.addEventListener("rca:installable", rerender);
    window.addEventListener("rca:installed", rerender);
    return () => {
      window.removeEventListener("rca:installable", rerender);
      window.removeEventListener("rca:installed", rerender);
    };
  }, []);

  const handle = async () => {
    const outcome = await promptInstall();
    if (outcome === "unavailable") {
      toast("Install the Rosemoor app", {
        description:
          "iPhone/iPad: tap Share, then \u201CAdd to Home Screen.\u201D Android/Chrome & Edge: open the browser menu, then \u201CInstall app.\u201D If it's already installed, open it from your home screen.",
        duration: 8000,
      });
    }
  };

  const label = canInstall() ? "Download this app" : "Download this app";

  if (variant === "light") {
    return (
      <button data-testid="install-app-persistent" onClick={handle}
        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 min-h-[42px] rounded-xl border border-ink/15 text-ink/75 hover:border-maroon/40 text-[14px] font-semibold">
        <Download className="w-4 h-4" /> {label}
      </button>
    );
  }

  if (variant === "mobile-list") {
    return (
      <button
        data-testid="install-app-persistent"
        onClick={handle}
        className="w-full flex items-center gap-2 text-[14px] font-semibold text-ink/80 hover:text-ink py-2"
      >
        <Download className="w-4 h-4" /> {label}
      </button>
    );
  }

  return (
    <button
      data-testid="install-app-persistent"
      onClick={handle}
      className="w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 min-h-[42px] text-[13px] font-semibold bg-cream/10 text-cream/90 hover:bg-cream/20 transition-colors"
    >
      <Download className="w-4 h-4" /> {label}
    </button>
  );
}
